import { defineTheme, DEFAULT_ICONS, type IconName, type ThemeIcon } from '../contract';
import { SOUND_EVENTS, type SoundPack } from '../../audio/sound-pack';
import { packAssets } from '../assets';
import './styles.css';
import license from './fonts/OFL-SpaceGrotesk.txt?raw';

const asset = packAssets(
  import.meta.glob('./{images,icons,audio}/*', { eager: true, query: '?url', import: 'default' }),
);
const texture = (name: string) => `url("${asset(`images/${name}.svg`)}")`;

export default defineTheme({
  id: 'neon-grid',
  name: 'Neon Grid',
  colorScheme: 'dark',
  description:
    'Jack into the midnight word circuit. Neon rain, etched processors, electric trails, and a low synth pulse.',
  credits:
    'Original Spellie illustration, icons and synthesized music. Space Grotesk by Florian Karsten, SIL OFL 1.1.',
  license,
  preview: asset('images/city.svg'),
  tokens: {
    background: '#090f21',
    foreground: '#d9faff',
    surface: '#101d30',
    surfaceText: '#d9faff',
    mutedText: '#9eb8cd',
    border: '#37617a',
    accent: '#60f4ed',
    primary: 'linear-gradient(110deg, #56f7e1, #58caed)',
    primaryText: '#061522',
    selected: '#23435a',
    selectedText: '#97fff6',
    tile: 'linear-gradient(145deg, #183044, #101d30)',
    tileText: '#c9faff',
    tileSelected: 'linear-gradient(135deg, #3bdacb, #62f4ed)',
    tileSelectedText: '#061a26',
    tileHead: 'linear-gradient(135deg, #ed73e1, #ffc0f0)',
    tileHeadText: '#25112e',
    success: '#73ffd0',
    connectionConnected: '#73ffd0',
    podiumGold: '#ffd166',
    podiumSilver: '#c5d6e6',
    podiumBronze: '#ff9f6b',
    winnerBackground: 'linear-gradient(110deg, #2b2a1e, #101d30 75%)',
    winnerShadow: '0 0 16px #ffd16655',
    connectionReconnecting: '#ffe066',
    connectionReconnectingAnimation: 'neon-connection-pulse 1.2s ease-in-out infinite',
    danger: '#ff9ab5',
    highlight: '#213d54',
    path: '#ff7edf',
    overlay: '#07112280',
    focus: '#ff8be9',
    font: '"Neon Space", Arial, sans-serif',
    boardFont: '"Neon Space", Arial, sans-serif',
    displayFont: '"Neon Space", Arial, sans-serif',
    fontWeight: '500',
    headingWeight: '700',
    letterSpacing: '0.025em',
    surfaceShadow: '0 10px 35px #00091488, inset 3px 0 0 #48ded8, inset -1px 0 0 #c64ca5',
    controlShadow: '3px 3px 0 #214555',
    tileShadow: '0 3px 0 #040e19, inset 0 0 0 1px #376078',
    cardRadius: '2px 22px 2px 2px',
    buttonRadius: '2px 12px 2px 2px',
    tileRadius: '3px 18% 3px 3px',
    inputRadius: '2px 9px 2px 2px',
    chipRadius: '2px 8px 2px 2px',
    dialogRadius: '2px 24px 2px 2px',
    sheetRadius: '2px 24px 0 0',
    cardBorder: '1px solid #38627d',
    buttonBorder: '1px solid #488395',
    tileBorder: '1px solid #428195',
    inputBorder: '1px solid #488395',
    buttonBackground: 'linear-gradient(120deg, #142c3e, #112034)',
    inputBackground: '#081827',
    cardTextureSize: '120px 120px',
    buttonTextureSize: '100px 100px',
    hudBackground: '#101d30',
    feedbackBackground: '#101d30',
    dialogBackground: '#091629',
    dialogText: '#d9faff',
    dialogTexture: texture('modal-hologram'),
    cardTexture: texture('circuit'),
    tileTexture: texture('chip'),
    buttonTexture: texture('circuit'),
    buttonHover: '#284858',
    buttonPress: 'translate(2px, 2px)',
    buttonHoverTransform: 'translateX(2px)',
    tileSelectTransform: 'scale(0.94)',
    tileSelectedShadow: '0 0 18px #4ff2e780',
    tileHeadOutline: '2px solid #ffc8f1',
    tileHeadShadow: '0 0 20px #f35ad590',
    pathWidth: '4',
    pathDash: '7 2',
    pathGlow: 'drop-shadow(0 0 3px #ff5fd9)',
    pathAnimation: 'neon-stream 600ms linear infinite',
    interactionDuration: '85ms',
    interactionEasing: 'steps(3, end)',
    spinnerTrack: 'color-mix(in srgb, var(--theme-accent) 25%, transparent)',
    spinnerColor: 'var(--theme-accent)',
    spinnerRadius: '20%',
    spinnerAnimation: 'neon-spinner 1s steps(8, end) infinite',
    enterAnimation: 'neon-boot 280ms ease-out both',
    revealAnimation: 'neon-decode 360ms ease-out both',
    selectAnimation: 'neon-charge 180ms ease-out',
    countdownAnimation: 'neon-count 850ms ease-out both',
    urgentAnimation: 'neon-urgent 800ms ease-in-out infinite alternate',
    acceptAnimation: 'neon-accept 220ms ease-out both',
    powerUpFeedbackBorder: '3px solid currentColor',
    powerUpFeedbackShadow:
      '0 0 14px color-mix(in srgb, currentColor 55%, transparent), inset 0 0 8px color-mix(in srgb, currentColor 20%, transparent)',
    powerUpCollectAnimation: 'neon-power-up-collect 600ms steps(4, end)',
    powerUpHitAnimation: 'neon-power-up-hit 600ms steps(4, end)',
    powerUpBlockedAnimation: 'neon-power-up-blocked 700ms steps(4, end)',
    rejectAnimation: 'neon-reject 280ms steps(3, end) both',
    duplicateAnimation: 'neon-duplicate 240ms ease-out both',
    resultsAnimation: 'neon-boot 500ms ease-out both',
    dialogAnimation: 'neon-boot 250ms ease-out both',
    welcomeAnimation: 'neon-open 220ms ease-out',
    decorationAnimation: 'neon-hover 5s ease-in-out infinite alternate',
    titleShadow: '2px 2px 0 #bd4fbb, 0 0 24px #32ccdf55',
    titleTransform: 'uppercase',
  },
  icons: Object.fromEntries(
    Object.keys(DEFAULT_ICONS).map((name) => [
      name,
      { src: asset(name === 'logo' ? 'images/emblem.svg' : `icons/${name}.svg`) },
    ]),
  ) as Record<IconName, ThemeIcon>,
  background: [
    {
      src: asset('images/city.svg'),
      mobileSrc: asset('images/city-portrait.svg'),
      depth: 4,
      position: 'center bottom',
    },
    { src: asset('images/rain.svg'), depth: 9, opacity: 0.6, animation: 'neon-rain 4s linear infinite' },
  ],
  sounds: Object.fromEntries(
    [...SOUND_EVENTS, 'music', 'ambience'].map((event) => [
      event,
      {
        src: asset(`audio/${event}.wav`),
        volume: event === 'music' ? 0.55 : event === 'ambience' ? 0.45 : 0.65,
      },
    ]),
  ) as SoundPack,
  // Circuit pickups: hot data blocks, each with its own flicker, scan or rotation.
  powerUps: {
    gold: {
      glyph: '"◆"',
      glyphColor: '#3a2a00',
      background: 'linear-gradient(135deg, #ffe066, #ff9f1c)',
      text: '#2a1c00',
      border: '1px solid #ffe9a0',
      shadow: '0 0 20px #ffb02f99',
      animation: 'neon-power-flicker 700ms steps(2, end) infinite',
    },
    double: {
      glyph: '"×2"',
      glyphColor: '#ffe6fb',
      background: 'linear-gradient(135deg, #ff5fd9, #a23bff)',
      text: '#fff3fd',
      border: '1px solid #ffb3f2',
      shadow: '0 0 22px #ff5fd9aa',
      animation: 'neon-power-surge 900ms ease-in-out infinite alternate',
    },
    reveal: {
      glyph: '"◉"',
      glyphColor: '#062226',
      background: 'linear-gradient(180deg, #60f4ed 0%, #1cb8c7 50%, #60f4ed 100%)',
      backgroundSize: '100% 200%',
      text: '#062226',
      border: '1px solid #b8fffb',
      animation: 'neon-power-scan 1.2s linear infinite',
    },
    shield: {
      glyph: '"▣"',
      glyphColor: '#c9faff',
      background: 'linear-gradient(145deg, #183044, #101d30)',
      text: '#c9faff',
      border: '2px solid #60f4ed',
      shadow: 'inset 0 0 0 4px #101d30, inset 0 0 0 5px #60f4ed, 0 0 14px #60f4ed88',
      animation: 'neon-power-armor 1.4s ease-in-out infinite alternate',
    },
    freeze: {
      glyph: '"❄"',
      glyphColor: '#0a2a44',
      background: 'linear-gradient(135deg, #e4fbff, #8fd8ff)',
      text: '#083050',
      border: '1px solid #ffffff',
      shadow: '0 0 18px #9fe3ffcc',
      animation: 'neon-power-flicker 1.1s steps(3, end) infinite',
    },
    tax: {
      glyph: '"½"',
      glyphColor: '#ffd6e0',
      background: 'linear-gradient(135deg, #7a1230, #3a0a18)',
      text: '#ffd6e0',
      border: '1px solid #ff9ab5',
      shadow: '0 0 16px #ff4d7a88',
      animation: 'neon-power-alarm 600ms steps(2, end) infinite',
    },
    spin: {
      glyph: '"↻"',
      glyphColor: '#f3e6ff',
      background: 'conic-gradient(from 0deg, #a23bff, #60f4ed, #ff5fd9, #a23bff)',
      text: '#ffffff',
      border: '1px solid #e0c2ff',
      animation: 'neon-power-rotate 2.4s linear infinite',
    },
    fog: {
      glyph: '"▒"',
      glyphColor: '#c7d3dd',
      background: 'repeating-linear-gradient(135deg, #3b4c5c 0 4px, #24323f 4px 8px)',
      text: '#dbe6ee',
      border: '1px solid #7b93a6',
      animation: 'neon-power-static 400ms steps(4, end) infinite',
    },
  },
  haptics: {
    select: [9],
    backtrack: [5],
    accept: [12, 20, 18],
    reject: [16, 20, 16],
    duplicate: [8],
    countdown: [10],
    start: [20, 20, 35],
    end: [40],
    win: [20, 25, 20, 25, 40],
    results: [18],
    powerUpGold: [10, 15, 10, 15, 30],
    powerUpDouble: [15, 20, 15],
    powerUpReveal: [6, 12, 6],
    powerUpShield: [25, 20, 25],
    powerUpFreeze: [45],
    powerUpTax: [12, 12, 12, 12, 12],
    powerUpSpin: [5, 10, 5, 10, 5, 10, 5, 10],
    powerUpFog: [20, 30, 20],
    powerUpHit: [30, 25, 20],
    powerUpBlocked: [15, 10, 15],
  },
});
